import { NgForOf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { BaseComponent } from '../../shared/base.component';
import { ViewCustomersState } from '../../shared/state-interface';
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { Customer } from '../shared/customer.model';
import { CustomerService } from '../shared/customer.service';

@Component({
    selector: 'app-view-customers',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatExpansionModule,
        MatTableModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        NgForOf,
        SiteHeaderComponent,
        DealerNamesComponent
    ],
    templateUrl: './view-customers.component.html',
    styleUrl: './view-customers.component.css'
})
export class ViewCustomersComponent extends BaseComponent implements AfterViewInit {

    @ViewChild(DealerNamesComponent) dealerNamesComponent!:DealerNamesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private customerService: CustomerService = inject(CustomerService);

    private static _retrievingCustomers = "Retrieving Customers";
    private static _retrievingCustomersError = `${ViewCustomersComponent._retrievingCustomers} error:`;
    private static _inactivatingCustomer = "Inactivating Customer";
    private static _inactivatingCustomerError = `${ViewCustomersComponent._inactivatingCustomer} error:`;
    private static _reactivatingCustomer = "Reactivating Customer";
    private static _reactivatingCustomerError = `${ViewCustomersComponent._reactivatingCustomer} error:`;
    private static _deletingCustomer = "Deleting Customer";
    private static _deletingCustomerError = `${ViewCustomersComponent._deletingCustomer} error:`;

    protected viewCustomersForm = new FormGroup({
        lastName: new FormControl(""),
        firstName: new FormControl(""),
        businessName: new FormControl(""),
        email: new FormControl(""),
        customerId: new FormControl("")
    });

    private _customers: WritableSignal<Customer[]> = signal<Customer[]>([]);
    private _customerCount: WritableSignal<number> = signal<number>(-1);

    public ngAfterViewInit() {
        // The <app-dealer-names-component/> will not be rendered
        // in the HTML for some user types.
        //
        // The dealerNamesComponent will be undefined if
        // <app-dealer-names-component/> is not rendered in the HTML.

        const state: ViewCustomersState | undefined = this.stateManager.viewCustomersState;

        if (state != undefined) {
            this.viewCustomersForm = state.viewCustomersForm;
            if (this.dealerNamesComponent != undefined) {
                this.dealerNamesComponent.restoreState(state.dealerNamesComponentState!);
            }

            if (this.refresh) {
                this.pageIndex = 0;
                this.customerCount = -1;
                this.listCustomers(this.overrideSnackBar);
            } else {
                this.customers = state.customers;
                this.customerCount = state.customerCount;
                super.restoreState(state.baseComponentState);
            }
        } else if (this.dealerNamesComponent) {
            this.dealerNamesComponent.init();
        }
    }

    public ngOnDestroy() {
        const state: ViewCustomersState = {} as ViewCustomersState;
        state.viewCustomersForm = this.viewCustomersForm;
        state.customers = this.customers;
        state.customerCount = this.customerCount;
        state.dealerNamesComponentState = this.dealerNamesComponent != undefined ?
                                            this.dealerNamesComponent.getState() : undefined;
        state.baseComponentState = super.getState();
        this.stateManager.viewCustomersState = state;
    }

    protected getAltCssClass(className: string): string {
        return (this.userType != "employee") ? className + "-alt" : "";
    }

    protected get customers(): Customer[] {
        return this._customers();
    }

    protected get customerCount(): number {
        return this._customerCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        super.handlePageEvent(e);
        this.listCustomers();
    }

    protected async listCustomers(): Promise<void>;
    protected async listCustomers(overrideSnackBar: string | undefined): Promise<void>;
    protected async listCustomers(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this.customerCount = -1;
                this.markSearchParams(super.pristine);
            }

            const observable = await this.customerService.getCustomers(await getIdToken(currentUser),
                this.getSearchParams(), this.pageSize, this.pageIndex);

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewCustomersComponent._retrievingCustomers}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-customers"] == "success") {
                        // this.customers is a signal. The view will be refreshed
                        // upon update to the signal.
                        this.customers = Customer.toCustomers(json["customers"]);
                        this.customerCount = json["customer-count"];
                        this.message = "";
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewCustomersComponent._retrievingCustomersError} ${(<Error>error).message}`;
                    super.message = message;
                }
            })
        } else {
            const message = `${ViewCustomersComponent._retrievingCustomersError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        this.lastNameCtl.setValue("");
        this.firstNameCtl.setValue("");
        this.businessNameCtl.setValue("");
        this.emailCtl.setValue("");
        this.customerIdCtl.setValue("");
        
        if (this.dealerNamesComponent != undefined) {
            this.dealerNamesComponent.dealerCtl.setValue("");
        }

        this.markSearchParams(super.dirty);
        this.listCustomers();
    }

    protected async addCustomer() {
        this.stateManager.addCustomerState = undefined;
        this.router.navigate(["/add-customer"]);
    }

    protected async updateCustomer(id: number) {
        this.stateManager.updateCustomerState = undefined;
        this.router.navigate([`/update-customer/${id}`]);
    }
    
    protected async inactivateCustomer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.customerService.inactivateCustomer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewCustomersComponent._inactivatingCustomer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["inactivate-customer"] == "success") {
                        this.stateManager.clearCustomerNames();
                        this.listCustomers("Customer inactivated");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewCustomersComponent._inactivatingCustomerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewCustomersComponent._inactivatingCustomerError} Null user instance`;
            console.error(message);
            super.showSnackBar(message, 30);
        }
    }

    protected async reactivateCustomer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.customerService.reactivateCustomer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewCustomersComponent._reactivatingCustomer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["reactivate-customer"] == "success") {
                        this.stateManager.clearCustomerNames();
                        this.listCustomers("Customer Reactivated");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewCustomersComponent._reactivatingCustomerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewCustomersComponent._reactivatingCustomerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async deleteCustomer(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.customerService.deleteCustomer(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewCustomersComponent._deletingCustomer}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["delete-customer"] == "success") {
                        if (this.customers.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }
                        this.stateManager.clearCustomerNames();
                        this.listCustomers("Customer deleted");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewCustomersComponent._deletingCustomerError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewCustomersComponent._deletingCustomerError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        return this.lastNameCtl.dirty
            || this.firstNameCtl.dirty
            || this.businessNameCtl.dirty
            || this.emailCtl.dirty
            || (this.dealerNamesComponent !== undefined ? this.dealerNamesComponent.dealerCtl.dirty : false)
            || this.customerIdCtl.dirty;
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.lastNameCtl, func);
        super.markControl(this.firstNameCtl, func);
        super.markControl(this.businessNameCtl, func);
        super.markControl(this.emailCtl, func);
        super.markControl(this.customerIdCtl, func);

        if (this.dealerNamesComponent != undefined) {
            super.markControl(this.dealerNamesComponent.dealerCtl, func);
        }
    }

    private getSearchParams(): any {
        return {
            "lastName": this.lastNameCtl.value,
            "firstName": this.firstNameCtl.value,
            "businessName": this.businessNameCtl.value,
            "email": this.emailCtl.value,
            "dealerId": (this.dealerNamesComponent != undefined
                        && this.dealerNamesComponent.selectedDealer > 0 ?
                            this.dealerNamesComponent.selectedDealer : undefined),
            "customerId": this.customerIdCtl.value
        }
    }

    private get lastNameCtl(): FormControl {
        return this.viewCustomersForm.controls.lastName;
    }

    private get firstNameCtl(): FormControl {
        return this.viewCustomersForm.controls.firstName;
    }

    private get businessNameCtl(): FormControl {
        return this.viewCustomersForm.controls.businessName;
    }

    private get emailCtl(): FormControl {
        return this.viewCustomersForm.controls.email;
    }

    private get customerIdCtl(): FormControl {
        return this.viewCustomersForm.controls.customerId;
    }

    private set customers(value: Customer[]) {
        this._customers.set(value);
    }

    private set customerCount(value: number) {
        this._customerCount.set(value);
    }
}
